import { LanguageToolApi, LanguageToolConfigParams, RulesConfig } from "./types";
import md5 from 'md5'
import { UUID } from "../../utils/uuid";

export interface LanguageToolClient {
  getLanguageToolResults(language: string, text: string, rules: RulesConfig): Promise<LanguageToolApi | null | undefined>;  
}

export class LanguageToolClientImpl implements LanguageToolClient {

  private cache : Map<string, LanguageToolApi>

  constructor(private config: LanguageToolConfigParams) {
    this.cache = new Map()
  }

  public async getLanguageToolResults(language: string, text: string, rules: RulesConfig) {
    
    const hash = `${md5(language)}-${md5(text)}-${md5(JSON.stringify(rules))}`
    if (this.cache.has(hash)) {
      return Promise.resolve(this.cache.get(hash))
    }

    try {
      const response = await fetch(this.config.server + "/v2/check", this.getRequestOptions(language, text, rules));

      if (!response.ok) {
        console.error("Error fetching LanguageTool results:", response.statusText);
        return null;
      }

      const json = (await response.json()) as LanguageToolApi;

      if (json.matches) {
        for(const match of json.matches) {
          match.uuid = UUID.generate()
          match.segmentType = "SpellCheck"
        }
      }      

      this.cache.set(hash, json)

      return json;
    } catch (e) {
      return null;
    }
  }

  private getRequestOptions(language: string, text: string, rules: RulesConfig) {

    return {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      mode: "cors" as RequestMode,
      body: this.encodeParams(language, text, rules),
    }
  }

  private encodeParams(language: string, text: string, rules: RulesConfig) {

    const validRulesObject = this.getValidRules(rules)

    const params = {
      level: 'picky',
      language,
      ...validRulesObject,
      ...this.config.apiOptions,
      text
    };

    return Object.keys(params)
      .map((key) => {
        const k = key as keyof typeof params
        const value = params[k]
        return value ? `${key}=${encodeURIComponent(value.toString())}` : ''
      } )
      .join("&");
  }


  private getValidRules(rules : RulesConfig) : Partial<RulesConfig> {
    const validRulesObject : Partial<RulesConfig> = {}
    Object.keys(rules).forEach(k => {
      const r = rules[k as keyof RulesConfig]
      if (r.length) {
        validRulesObject[k as keyof RulesConfig] = rules[k as keyof RulesConfig]
      }
    })

    return validRulesObject
  }
}
